import { useEffect, useMemo, useState } from 'react'
import { Calculator, Minus, Plus, Search, Share2, X } from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { Boton, Entrada } from './ui/base'
import { Hoja } from './ui/Hoja'
import { cn } from '../lib/cn'
import { money, normalizar, num, pesoLabel } from '../lib/format'
import { copiarTexto } from '../lib/portapapeles'
import {
  conIva,
  esVolumen,
  kilosLinea,
  montoIva,
  precioLista,
  tienePrecioPropio,
} from '../lib/precios'
import type { TipoCliente } from '../types'

/** Calculadora de precios: arma un pedido de mentira y muestra cuánto
 *  saldría, con la lista que le toca a ese tipo de cliente. No toca
 *  stock, no crea nada —sirve para responder por teléfono "¿cuánto me
 *  saldrían 12 kilos?" sin tener que abrir una venta.
 *
 *  En la versión de empresas el número que importa es doble: el neto es
 *  con el que se cierra el trato, y el con IVA es el que va a salir en
 *  la factura. Los dos se muestran juntos para que nadie tenga que
 *  multiplicar por 1,19 de memoria. */
export function SimuladorPrecios({
  tipo,
  onCerrar,
}: {
  tipo: TipoCliente | null
  onCerrar: () => void
}) {
  const { productos, ajustes } = useDatos()
  const vendedor = useSesion((s) => s.usuario?.nombre)
  const avisar = useUI((s) => s.avisar)
  const iva = ajustes.porcentajeIva || 19
  const [cantidades, setCantidades] = useState<Record<string, number>>({})
  const [precios, setPrecios] = useState<Record<string, number>>({})
  const [texto, setTexto] = useState('')

  // Cada vez que se abre parte en blanco: es una consulta suelta, no
  // algo que valga la pena recordar de una vez para otra.
  useEffect(() => {
    if (tipo) {
      setCantidades({})
      setPrecios({})
      setTexto('')
    }
  }, [tipo])

  const lista = useMemo(() => {
    const q = normalizar(texto.trim())
    return productos
      .filter((p) => p.activo)
      .filter((p) => !q || [p.nombre, p.codigo].some((c) => c && normalizar(c).includes(q)))
      .sort((a, b) => {
        // Lo que ya está en la simulación queda arriba, para no perderlo
        // de vista mientras se busca lo siguiente.
        const ca = cantidades[a.id] ?? 0
        const cb = cantidades[b.id] ?? 0
        if (ca > 0 !== cb > 0) return ca > 0 ? -1 : 1
        const oa = a.ordenCatalogo ?? Infinity
        const ob = b.ordenCatalogo ?? Infinity
        return oa !== ob ? oa - ob : a.nombre.localeCompare(b.nombre, 'es')
      })
  }, [productos, texto, cantidades])

  const filas = useMemo(() => {
    if (!tipo) return []
    return productos
      .filter((p) => (cantidades[p.id] ?? 0) > 0)
      .map((p) => {
        const cantidad = cantidades[p.id]
        const sugerido = precioLista(p, tipo, cantidad)
        const precio = precios[p.id] ?? sugerido
        return {
          producto: p,
          cantidad,
          precio,
          volumen: esVolumen(cantidad, p, tipo),
          kilos: kilosLinea(cantidad, p),
          subtotal: cantidad * precio,
        }
      })
  }, [productos, cantidades, precios, tipo])

  const neto = filas.reduce((s, f) => s + f.subtotal, 0)
  const kilos = filas.reduce((s, f) => s + f.kilos, 0)
  const esEmpresa = tipo === 'empresa'

  /** La cotización en texto plano, lista para pegar en WhatsApp. Va sin
   *  tablas ni caracteres raros a propósito: tiene que leerse igual en
   *  el teléfono más viejo del cliente. */
  const textoCotizacion = () => {
    const partes = [
      'NovaBerry · Cotización',
      esEmpresa ? 'Precios mayoristas (valores netos)' : 'Precios al detalle',
      '',
      ...filas.map(
        (f) =>
          `• ${f.producto.nombre} — ${num(f.cantidad)} × ${money(f.precio)} = ${money(f.subtotal)}`,
      ),
      '',
    ]
    if (esEmpresa) {
      partes.push(
        `Neto: ${money(neto)}`,
        `IVA ${iva}%: ${money(montoIva(neto, iva))}`,
        `TOTAL CON IVA: ${money(conIva(neto, iva))}`,
      )
    } else {
      partes.push(`TOTAL: ${money(neto)}`)
    }
    if (vendedor) partes.push('', `Te atiende: ${vendedor}`)
    return partes.join('\n')
  }

  const compartir = async () => {
    const texto = textoCotizacion()
    // En teléfono se abre la hoja de compartir del sistema (WhatsApp,
    // correo, lo que tenga instalado). En un computador esa API no
    // existe casi nunca, así que se copia al portapapeles y, si hasta
    // eso falla, se abre WhatsApp Web con el texto ya escrito.
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Cotización NovaBerry', text: texto })
        return
      } catch (err) {
        // Cancelar la hoja de compartir no es un error que valga la
        // pena avisar: la persona simplemente se arrepintió.
        if (err instanceof DOMException && err.name === 'AbortError') return
      }
    }
    if (await copiarTexto(texto)) {
      avisar('Cotización copiada. Pégala en WhatsApp.')
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank')
    }
  }

  const cambiar = (id: string, delta: number) =>
    setCantidades((c) => {
      const nueva = Math.max(0, (c[id] ?? 0) + delta)
      const copia = { ...c }
      if (nueva === 0) delete copia[id]
      else copia[id] = nueva
      return copia
    })

  return (
    <Hoja
      abierta={tipo !== null}
      onCerrar={onCerrar}
      titulo={esEmpresa ? 'Simulador · precios empresa' : 'Simulador · precios particular'}
      descripcion="Solo calcula. No registra ninguna venta ni descuenta stock."
      ancho="md"
      pie={
        <div className="w-full">
          {filas.length === 0 ? (
            <p className="text-center text-[13px] texto-tenue">
              Suma productos para ver cuánto sale.
            </p>
          ) : (
            <div className="space-y-2">
              {esEmpresa ? (
                <>
                  <FilaTotal etiqueta="Neto (sin IVA)" valor={money(neto)} />
                  <FilaTotal etiqueta={`IVA ${iva}%`} valor={money(montoIva(neto, iva))} tenue />
                  <div className="flex items-baseline justify-between border-t borde-suave pt-2">
                    <span className="text-[14px] font-bold texto-base">Total con IVA</span>
                    <span className="cifra font-display text-2xl font-bold texto-base">
                      {money(conIva(neto, iva))}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-baseline justify-between">
                  <span className="text-[14px] font-bold texto-base">Total</span>
                  <span className="cifra font-display text-2xl font-bold texto-base">
                    {money(neto)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3 pt-1">
                <div className="min-w-0">
                  <p className="text-[12px] texto-tenue">
                    {filas.length} {filas.length === 1 ? 'producto' : 'productos'}
                    {kilos > 0 && ` · ${num(Math.round(kilos * 100) / 100)} kg`}
                  </p>
                  <button
                    onClick={() => {
                      setCantidades({})
                      setPrecios({})
                    }}
                    className="text-[12px] font-semibold texto-suave transition-colors hover:texto-base"
                  >
                    Limpiar
                  </button>
                </div>
                <Boton
                  className="ml-auto"
                  variante="secundario"
                  icono={<Share2 className="size-[18px]" />}
                  onClick={compartir}
                >
                  Compartir
                </Boton>
              </div>
            </div>
          )}
        </div>
      }
    >
      <div className="sticky top-0 z-10 -mx-5 mb-2 superficie-elevada px-5 pb-3 sm:-mx-6 sm:px-6">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-4 size-[18px] -translate-y-1/2 texto-tenue" />
          <Entrada
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar producto…"
            className="pr-11 pl-11"
          />
          {texto && (
            <button
              onClick={() => setTexto('')}
              className="absolute top-1/2 right-3 grid size-8 -translate-y-1/2 place-items-center rounded-lg texto-tenue"
              aria-label="Borrar"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {lista.length === 0 ? (
        <p className="py-10 text-center text-sm texto-tenue">Ningún producto coincide.</p>
      ) : (
        <ul className="divide-y borde-suave">
          {lista.map((p) => {
            const cantidad = cantidades[p.id] ?? 0
            const sugerido = precioLista(p, tipo ?? 'particular', Math.max(cantidad, 1))
            const precio = precios[p.id] ?? sugerido
            const volumen = esVolumen(Math.max(cantidad, 1), p, tipo ?? undefined)
            return (
              <li key={p.id} className="py-3">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-semibold texto-base">{p.nombre}</p>
                    <p className="mt-0.5 text-[12px] texto-suave">
                      {p.peso > 0 && `${pesoLabel(p)} · `}
                      <span className="cifra font-semibold">{money(sugerido)}</span>
                      {esEmpresa && (
                        <span className="texto-tenue"> · con IVA {money(conIva(sugerido, iva))}</span>
                      )}
                      {esEmpresa && !tienePrecioPropio(p, 'empresa') && (
                        <span className="texto-tenue"> · precio detalle</span>
                      )}
                      {volumen && cantidad > 0 && (
                        <span className="text-lima-600 dark:text-lima-300"> · +10 kg</span>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 rounded-2xl superficie-hundida p-1">
                    <button
                      onClick={() => cambiar(p.id, -1)}
                      disabled={cantidad === 0}
                      className="grid size-9 place-items-center rounded-xl superficie-elevada texto-base transition-transform active:scale-90 disabled:opacity-30"
                      aria-label={`Restar ${p.nombre}`}
                    >
                      <Minus className="size-4" />
                    </button>
                    <span
                      className={cn(
                        'cifra w-9 text-center text-[16px] font-bold',
                        cantidad > 0 ? 'texto-base' : 'texto-tenue',
                      )}
                    >
                      {cantidad}
                    </span>
                    <button
                      onClick={() => cambiar(p.id, 1)}
                      className="grid size-9 place-items-center rounded-xl superficie-elevada texto-base transition-transform active:scale-90"
                      aria-label={`Sumar ${p.nombre}`}
                    >
                      <Plus className="size-4" />
                    </button>
                  </div>
                </div>

                {cantidad > 0 && (
                  <div className="mt-2.5 flex items-center gap-3 rounded-2xl superficie-hundida px-3 py-2 anim-aparecer">
                    <label className="flex items-center gap-2">
                      <span className="text-[12px] font-semibold texto-suave">Precio</span>
                      <input
                        value={precio}
                        onChange={(e) =>
                          setPrecios((v) => ({ ...v, [p.id]: Math.max(0, Number(e.target.value) || 0) }))
                        }
                        inputMode="numeric"
                        className="cifra h-9 w-24 rounded-xl border borde-base superficie-elevada px-3 text-[14px] font-semibold texto-base outline-none focus:border-bosque-400"
                        aria-label={`Precio de ${p.nombre}`}
                      />
                    </label>
                    <span className="ml-auto text-right">
                      <span className="cifra block text-[16px] font-bold texto-base">
                        {money(cantidad * precio)}
                      </span>
                      {esEmpresa && (
                        <span className="cifra block text-[11px] texto-tenue">
                          con IVA {money(conIva(cantidad * precio, iva))}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Hoja>
  )
}

function FilaTotal({
  etiqueta,
  valor,
  tenue,
}: {
  etiqueta: string
  valor: string
  tenue?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn('text-[13px]', tenue ? 'texto-tenue' : 'texto-suave')}>{etiqueta}</span>
      <span className="cifra text-[14px] font-semibold texto-base">{valor}</span>
    </div>
  )
}

/** Botón chico que abre el simulador. Va debajo de cada cuadro de tipo
 *  de cliente: se ve como una nota al pie, no compite con la acción
 *  principal (que es entrar a vender). */
export function BotonSimulador({
  onClick,
  tono,
}: {
  onClick: () => void
  tono: 'tinta' | 'magenta'
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border-2 px-4 py-2.5',
        'text-[13px] font-semibold transition-colors',
        // El marco queda del mismo color que el cuadro de arriba: es lo
        // que deja claro que este simulador pertenece a esa lista de
        // precios y no a la otra.
        tono === 'tinta'
          ? 'border-tinta-300 text-tinta-700 hover:bg-tinta-50/70 dark:border-tinta-500/40 dark:text-tinta-200 dark:hover:bg-tinta-500/10'
          : 'border-magenta-300 text-magenta-700 hover:bg-magenta-50/70 dark:border-magenta-500/40 dark:text-magenta-300 dark:hover:bg-magenta-500/10',
      )}
    >
      <Calculator className="size-4" />
      Simulador de precios
    </button>
  )
}
